/* eslint-disable camelcase */

/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext, useState } from 'react';

import Retracked from 'js/app/retracked';
import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Chip, ChipGroup, Grid, Typography, Typography2 } from '@coursera/cds-core';
import type { ChipProps, Theme } from '@coursera/cds-core';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import BrowseProjectProductCard from 'bundles/account-profile/components/recruiting-profile-viewer/components/projects/BrowseProjectsProductCard';
import { EVENTS } from 'bundles/account-profile/components/recruiting-profile-viewer/eventing/events';
import type { Occupation } from 'bundles/account-profile/components/recruiting-profile-viewer/utils/projectUtils';
import { getOccupationLabelWithId } from 'bundles/account-profile/components/recruiting-profile-viewer/utils/projectUtils';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import type { SharedPortfolioProject_ProjectDetailsFragment as PortfolioProjectDetails } from 'bundles/portfolio-projects/queries/__generated__/SharedPortfolioProject_ProjectDetails';

import _t from 'i18n!nls/account-profile';

const styles = {
  projectCard: (theme: Theme) => css`
    margin-bottom: ${theme.spacing(12)};
  `,
  smallBottomMargin: (theme: Theme) => css`
    margin-bottom: ${theme.spacing(8)};
  `,
  largeTopPadding: (theme: Theme) => css`
    padding-top: ${theme.spacing(48)};
  `,
};

type PropsFromCaller = {
  projects: PortfolioProjectDetails[];
  occupations: Occupation[];
  projectCardLinkTrackingName?: typeof EVENTS.profileBrowseProjectSelectProject;
};

const Wrapper: React.FC = ({ children }) => {
  return <>{children}</>;
};

type ProjectsEventingData = {
  projects_filters: Record<string, boolean>;
};

const TrackedWrapper = Retracked.createTrackedContainer<{ retrackedEventingData?: ProjectsEventingData }>((props) => ({
  ...props.retrackedEventingData,
}))(Wrapper);

const TrackedFilterChip = ({
  occupationLabel,
  occupationId,
  onClick,
  ...chipProps
}: ChipProps & { occupationLabel: string; occupationId: string }) => {
  const { sharedEventingV3Data } = useContext(ProfileContext);
  const track = useTracker();
  const visibilityTrackingRef: React.MutableRefObject<HTMLButtonElement | null> = useVisibilityTracker('view_button', {
    button: {
      name: EVENTS.browseProjectsFilterChip,
    },
    filter: [occupationId],
    ...sharedEventingV3Data,
  });

  return (
    <Chip
      {...chipProps}
      title={occupationLabel}
      onClick={(ev) => {
        track('click_button', {
          button: {
            name: EVENTS.browseProjectsFilterChip,
          },
          filter: [occupationId],
          ...sharedEventingV3Data,
        });

        onClick(ev);
      }}
      ref={visibilityTrackingRef}
    >
      {occupationLabel}
    </Chip>
  );
};

const ProjectCardsWithFilters = ({ projects, occupations, projectCardLinkTrackingName }: PropsFromCaller) => {
  const uniqueProjectsOccupationsId = [...new Set(projects.map((p) => p.occupationId))];
  const uniqueProjectsOccupations = uniqueProjectsOccupationsId
    .map(
      (occupationId: string) =>
        ({ occupationId, occupationLabel: getOccupationLabelWithId(occupationId, occupations) } as Record<
          'occupationId' | 'occupationLabel',
          string
        >)
    )
    .sort((occupationA, occupationB) => occupationA.occupationLabel.localeCompare(occupationB.occupationLabel));

  // We need to map these occupations as initial values so that the projects_filters eventing property contains all the filters we have
  const mappedOccupations = uniqueProjectsOccupationsId.reduce((acc, occupationId) => {
    return {
      ...acc,
      [occupationId]: false,
    };
  }, {} as Record<string, boolean>);

  const [activeFilters, setActiveFilters] = useState<Record<string, boolean>>(mappedOccupations);

  const hasActiveFilter = Object.values(activeFilters).some((isSelected) => isSelected);

  const toggleFilter = (occupationId: string) => {
    setActiveFilters((prevActiveFilters) => {
      return {
        ...prevActiveFilters,
        [occupationId]: !prevActiveFilters[occupationId],
      };
    });
  };

  const projectsToDisplay = hasActiveFilter
    ? projects.filter((project) => activeFilters[project.occupationId])
    : projects;

  return (
    <Grid container>
      <Grid container item>
        <Typography css={styles.smallBottomMargin} id="filterLabel" variant="h2semibold">
          {_t('Filter by role:')}
        </Typography>
        <ChipGroup variant="select" aria-labelledby="filterLabel" multiSelect>
          {uniqueProjectsOccupations.map(({ occupationId, occupationLabel }) => (
            <TrackedDiv
              key={occupationId}
              trackingName={EVENTS.browseProjectsFilterChip}
              trackClicks
              withVisibilityTracking
              data={{ filterOccupationId: occupationId }}
            >
              <TrackedFilterChip
                occupationLabel={occupationLabel}
                occupationId={occupationId}
                title={occupationLabel}
                onClick={() => toggleFilter(occupationId)}
                selected={activeFilters[occupationId]}
              >
                {occupationLabel}
              </TrackedFilterChip>
            </TrackedDiv>
          ))}
        </ChipGroup>
      </Grid>
      <TrackedWrapper retrackedEventingData={{ projects_filters: activeFilters }}>
        {projectsToDisplay.length === 0 && (
          <Grid container item css={styles.largeTopPadding} justifyContent="center" alignItems="center">
            <Typography2 component="p" color="supportText">
              <FormattedMessage
                message={_t('There are no projects for {numRoles, plural, =1 {that role} other {these roles}}.')}
                numRoles={Object.values(activeFilters).filter((isSelected) => isSelected).length}
              />
            </Typography2>
          </Grid>
        )}

        {projectsToDisplay.length > 0 && (
          <Grid container item direction="column" css={styles.largeTopPadding}>
            {projectsToDisplay.map((project, index) => {
              return (
                <Grid item key={project.id}>
                  <BrowseProjectProductCard
                    index={index}
                    trackingName={projectCardLinkTrackingName}
                    project={project}
                    occupations={occupations}
                    customCss={styles.projectCard}
                    activeFilters={activeFilters}
                  />
                </Grid>
              );
            })}
          </Grid>
        )}
      </TrackedWrapper>
    </Grid>
  );
};

export default ProjectCardsWithFilters;
